/**
 * Pure helpers for carrying out the help assistant's actions (D10-ACT-001/002,
 * #3605). The streamed action has already been validated twice -- by the server
 * and by `validateSupportAction` against the allowed lists the panel sent --
 * these are the last checks, against the live app, right before it runs.
 */
import { getTour } from "../tours/registry";
import { isTourAvailable, visibleSteps } from "../tours/filter";
import { stepRouteAction } from "../tours/routeMatch";
import type { TourGateContext } from "../tours/types";

export type TourLaunchCheck = "ok" | "unknown" | "unavailable" | "needs-record";

/**
 * Can this tour start for this user, from this page, right now?
 *
 *  - `unknown`      -- not in the tour registry (a stale or invented id);
 *  - `unavailable`  -- the live permission/module/audience gate hides it;
 *  - `needs-record` -- its first step lives on a record page (`:param` route)
 *                      the user is not on, so the engine would cancel it;
 *  - `ok`           -- `TourContext.startTour` will run it.
 */
export function checkTourLaunch(
  tourId: string,
  gate: TourGateContext,
  pathname: string,
): TourLaunchCheck {
  const tour = getTour(tourId);
  if (!tour) return "unknown";
  if (!isTourAvailable(tour, gate)) return "unavailable";
  const first = visibleSteps(tour, gate)[0];
  if (first && stepRouteAction(pathname, first) === "cancel") return "needs-record";
  return "ok";
}

/** What the user sees when a tour the assistant chose cannot start here. */
export function tourRefusalText(check: Exclude<TourLaunchCheck, "ok">): string {
  return check === "needs-record"
    ? "That tour starts from an open record. Open one first, then ask again."
    : "That tour isn't available to you.";
}

/**
 * The feedback form's pre-filled description: the assistant's one-line summary,
 * then the server-built transcript excerpt exactly as the server sent it --
 * already scrubbed, fenced and labelled as untrusted (D10-SEC-006). Never built
 * from the browser's own message list.
 */
export function buildFeedbackDescription(summary: string, fencedExcerpt: string): string {
  const head = summary.trim();
  const tail = fencedExcerpt.trim();
  if (!tail) return head;
  return head ? `${head}\n\n${tail}` : tail;
}
